import {
  HomeOwnerImportModel,
  RawHomeownerRow,
  RowValidationError,
  SystemReferenceData,
} from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/types';
import { ISuite } from 'src/components/SharedTypes';
import {
  trimOrEmpty,
  parseBoolean,
} from 'src/utils/CommonHelpers/sharedCSVUtils';
import { variables } from 'src/Tenants';

const VALID_ROLES = ['Home Owner', 'Realtor', 'Occupant'];

/* Map one raw CSV row to the API's HomeOwnerImportModel shape.
 * Column names mirror REQUIRED_COLUMNS exactly. */
const mapRowToModel = (row: RawHomeownerRow): HomeOwnerImportModel => ({
  FirstName: trimOrEmpty(row['First Name']),
  LastName: trimOrEmpty(row['Last Name']),
  Email: trimOrEmpty(row['Email']),
  Phone: trimOrEmpty(row['Phone']),
  PropertyName: trimOrEmpty(row['Property']),
  Building: trimOrEmpty(row['Building']),
  UnitNumber: trimOrEmpty(row['Unit']),
  IsPrimary: parseBoolean(row['Primary']),
  DashboardOnly: parseBoolean(row['Dashboard Only']),
  IsDesignated: parseBoolean(row['Designate']),
  Role: trimOrEmpty(row['Role']),
});

const isNonEmptyRow = (row: RawHomeownerRow): boolean =>
  Object.values(row).some((v: string) => v !== undefined && v.trim() !== '');

export const buildSystemReferenceData = (
  suites: ISuite[]
): SystemReferenceData => {
  const buildings = new Set<string>();
  const unitNumbers = new Set<string>();
  const propertyNames = new Set<string>();

  for (const suite of suites) {
    if (suite.building) {
      buildings.add(suite.building.trim().toLowerCase());
    }

    if (suite.number) {
      unitNumbers.add(suite.number.trim().toLowerCase());
    }

    if (suite.propertyName) {
      propertyNames.add(suite.propertyName.trim().toLowerCase());
    }
  }

  return { buildings, unitNumbers, propertyNames };
};

export const validateHomeownerRows = (
  records: HomeOwnerImportModel[],
  referenceData: SystemReferenceData
): RowValidationError[] => {
  const { buildings, unitNumbers, propertyNames } = referenceData;
  const errors: RowValidationError[] = [];

  records.forEach((record: HomeOwnerImportModel, index: number) => {
    const issues: string[] = [];

    if (!record.Role || record.Role.trim() === '') {
      issues.push(`Role is missing. Each row must have a Role.`);
    } else if (!VALID_ROLES.includes(record.Role.trim())) {
      issues.push(`Role "${record.Role}" is not valid. Must be one of: ${VALID_ROLES.join(', ')}.`);
    }

    if (!buildings.has(record.Building.trim().toLowerCase())) {
      issues.push(`Building "${record.Building}" doesn't exist in the system.`);
    }

    if (!unitNumbers.has(record.UnitNumber.trim().toLowerCase())) {
      issues.push(`Unit "${record.UnitNumber}" doesn't exist in the system.`);
    }

    if (!propertyNames.has(record.PropertyName.trim().toLowerCase())) {
      issues.push(`Property "${record.PropertyName}" doesn't exist in the system.`);
    }

    if (issues.length > 0) {
      errors.push({ row: index + 1, email: record.Email, issues });
    }
  });

  return errors;
};

const validatePrimaryHomeownerConstraint = (
  records: HomeOwnerImportModel[]
): RowValidationError[] => {
  const unitPrimaryIndices = new Map<string, number[]>();

  records.forEach((record: HomeOwnerImportModel, index: number) => {
    if (record.IsPrimary) {
      const unitKey: string = record.UnitNumber.trim().toLowerCase();
      const indices: number[] = unitPrimaryIndices.get(unitKey) || [];

      indices.push(index);
      unitPrimaryIndices.set(unitKey, indices);
    }
  });

  const errors: RowValidationError[] = [];

  unitPrimaryIndices.forEach((indices: number[], _unitKey: string) => {
    if (indices.length > 1) {
      indices.forEach((index: number) => {
        const record: HomeOwnerImportModel = records[index];

        errors.push({
          row: index + 1,
          email: record.Email,
          issues: [
            `Unit "${record.UnitNumber}" has ${indices.length} primary homeowners. You are only allowed to import one primary homeowner per ${variables.suite}.`,
          ],
        });
      });
    }
  });

  return errors;
};

export {
  parseBoolean,
  mapRowToModel,
  isNonEmptyRow,
  validatePrimaryHomeownerConstraint,
};
